/**
 * Near-duplicate material-name detection (design 2026-08-31, fuzzy matching):
 * the stock and delivery forms use these to surface an existing name before a
 * slightly different spelling of it gets minted as a new material. Pure and
 * dependency-free so both packages can share one notion of "the same name".
 */

import { fold } from './fold.js';

/** Separators people vary freely: ASCII/full-width spaces, hyphen family
 *  (hyphen, non-breaking, en/em dash, minus, full-width), middle dots,
 *  underscores. NFKC already collapses some full-width forms; the class keeps
 *  the originals too so the rule reads as what it means. */
const SEPARATORS = /[\s　_\-‐–—−－・･]/g;

/** The comparison key: folded, separators dropped — "Rebar D-16", "RebarD16"
 *  and "rebar d16" all become "rebard16". squash-equality is the strong
 *  "same name, spelled differently" signal. */
export function squash(name: string): string {
  return fold(name).replace(SEPARATORS, '');
}

export type NameMatch = 'exact' | 'close';

/**
 * How `typed` relates to `candidate`:
 * - `exact` — fold-equal: the identity rule already treats them as one name.
 * - `close` — squash-equal: same name once separators are ignored.
 * - `null`  — different names. (A typo-distance "similar" tier shipped and
 *   was removed the same day — Niv, 2026-08-31: a one-edit neighbour like
 *   "Rebar D19" for "Rebar D16" reads as a different product.)
 */
export function classifyNameMatch(typed: string, candidate: string): NameMatch | null {
  if (fold(typed) === fold(candidate)) return 'exact';
  return squash(typed) === squash(candidate) ? 'close' : null;
}

/** Every candidate worth offering for `typed` — the close-match names in
 *  source order. Exact matches are excluded (the caller's normal flow already
 *  handles those), and a query under two squashed characters matches nothing
 *  (all noise). */
export function findSimilarNames(typed: string, candidates: string[]): string[] {
  if (squash(typed).length < 2) return [];
  return candidates.filter((name) => classifyNameMatch(typed, name) === 'close');
}
